import classNames from 'classnames';
import { appInsight, collectProperties } from 'components/appInsight';
import Hls from 'hls.js';
import React, { CSSProperties, memo, useEffect, useMemo, useRef, useState } from 'react';
import styles from './LiveVideoStreaming.module.scss';
import { LiveVideoStreamingProps } from './types';

export const LiveVideoStreaming = memo(function LiveVideoStreaming({
  style,
  children,
  controls = true,
  className,
  isFullScreen,
  urlStreaming,
  isHeightAuto,
  autoPlayActive,
  availableControl,
  audioIsMute,
  ...oth
}: LiveVideoStreamingProps) {
  const videoEl = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const hls = useMemo(
    () =>
      new Hls({
        backBufferLength: 80,
        // debug: process.env.NODE_ENV !== 'production',
        enableWorker: true,
      }),
    []
  );

  useEffect(() => {
    const videoCurrentCopy = videoEl.current;
    if (videoCurrentCopy) {
      videoCurrentCopy.muted = audioIsMute ?? true;
      if (Hls.isSupported()) {
        hls.attachMedia(videoCurrentCopy);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          setIsVideoLoaded(true);
          hls.loadSource(urlStreaming);
          hls.startLoad();
          hls.on(Hls.Events.MANIFEST_PARSED, function () {
            const autoplay = videoCurrentCopy.play();
            if (autoplay !== undefined) {
              autoplay
                .then(() => {
                  videoCurrentCopy.play();
                })
                .catch((error) => {
                  videoCurrentCopy.play();
                });
            }
          });
        });
      } else if (videoEl.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoEl.current.src = urlStreaming;
        videoCurrentCopy
          ?.play()
          .then(() => {
            videoCurrentCopy.play();
          })
          .catch((error) => {
            videoCurrentCopy.play();
          });
        setIsVideoLoaded(true);
      } else {
        appInsight?.trackException({
          exception: new Error(`video non supported on this platform`),
          properties: collectProperties(true, {
            urlStreaming,
          }),
        });
      }
    }

    return () => {
      if (videoCurrentCopy && isVideoLoaded) {
        hls.stopLoad();
        hls.removeAllListeners();
        hls.detachMedia();
        hls.destroy();
      }
    };
  }, [hls, isVideoLoaded, urlStreaming]);

  useEffect(() => {
    const videoControl = videoEl.current;
    if (videoControl) {
      let pausedOn = 0;
      let pausedByUser = false;

      const handlePlayClick = () => {
        if (pausedByUser) {
          pausedByUser = false;
          let resumeTime = Math.floor((Date.now() - pausedOn) / 1000);
          videoControl.currentTime = videoControl.currentTime + resumeTime;
        }
      };

      const handlePauseClick = () => {
        pausedByUser = true;
        pausedOn = Date.now();
      };

      videoControl.addEventListener('play', handlePlayClick);
      videoControl.addEventListener('pause', handlePauseClick);

      return () => {
        videoControl.removeEventListener('play', handlePlayClick);
        videoControl.removeEventListener('pause', handlePauseClick);
      };
    }
    return undefined;
  }, []);

  const fullScreenStyle: CSSProperties = {
    width: '100%',
    height: isHeightAuto ? 'auto' : '100%',
    ...style,
  };

  return (
    <React.Fragment>
      <video
        {...oth}
        ref={videoEl}
        style={isFullScreen ? fullScreenStyle : style}
        controls={controls}
        autoPlay={autoPlayActive}
        className={classNames(className ? className : styles.video)}
        playsInline={true}
        crossOrigin="anonymous"
        controlsList={availableControl?.join(' ')}
        disablePictureInPicture
      />
      {children && children}
    </React.Fragment>
  );
});
